import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Horizontal scroll row.
 * - Mouse wheel translates vertical scroll into horizontal.
 * - Touch swipe works natively (overflow-x-auto).
 * - Optional left/right arrow controls.
 *
 * Children should be siblings (e.g. cards). Each child gets the layout via the
 * container's flex layout (parent applies the gap; children should not have margin).
 */
export default function HorizontalScrollRow({ children, ariaLabel = '', testId = 'h-scroll', className = '', step = 320 }) {
  const ref = useRef(null);

  const onWheel = (e) => {
    if (!ref.current) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      ref.current.scrollLeft += e.deltaY;
    }
  };

  const scroll = (dir) => {
    if (!ref.current) return;
    ref.current.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div className={`relative group ${className}`} data-testid={testId}>
      <button
        type="button"
        aria-label="Scroll left"
        data-testid={`${testId}-prev`}
        onClick={() => scroll(-1)}
        className="hidden md:grid place-items-center absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/70 backdrop-blur text-white border border-white/10 hover:bg-[#FF007F]/90 transition opacity-0 group-hover:opacity-100"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div
        ref={ref}
        onWheel={onWheel}
        aria-label={ariaLabel}
        className="flex gap-3 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-thin"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,0,127,0.4) transparent',
        }}
      >
        {children}
      </div>
      <button
        type="button"
        aria-label="Scroll right"
        data-testid={`${testId}-next`}
        onClick={() => scroll(1)}
        className="hidden md:grid place-items-center absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/70 backdrop-blur text-white border border-white/10 hover:bg-[#FF007F]/90 transition opacity-0 group-hover:opacity-100"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
